import { BsPlusLg, BsExclamationTriangle } from 'react-icons/bs'
import { v4 as uuidv4 } from 'uuid'
import ActionStatus from 'components/ActionStatus'
import { useFieldArray } from 'react-hook-form'
import { AddFields } from '../styles'
import { useEffect, useMemo, useState } from 'react'
import { WarningModal } from 'components/Modals/WarningModal'
import { hasNonEmptyValue } from 'utils/variblesHandler'
import NotificationActions from 'actions/Notification'
import { useDispatch } from 'react-redux'
import NotificationService from 'services/Notification'
import {
  IActionsComponent,
  FocusedInputType,
  ToDeleteElementType,
  listOfQuestions
} from '../helpers'
import { canEditActionPlan } from '..'
import ActionTable from './ActionTable'

export const ActionsComponent = ({
  id,
  tooltipAlert,
  register,
  control,
  reset,
  getValues,
  watch,
  errors,
  currentUser,
  dirtyFieldsStep4,
  setValue,
  resetField,
  isDirty,
  statusIsArchivedOrFinished,
  actionPlans,
  notificationById,
  notificationStatusState,
  trigger
}: IActionsComponent) => {
  //hooks
  const notificationDispatch = useDispatch()

  //state
  const [notAllowed, setNotAllowed] = useState(false)
  const [deleteWithTextModal, setDeleteWithTextModal] = useState(false)
  const [focusedInputData, setFocusedInputData] = useState<FocusedInputType>()
  const [getRemainChar, setGetRemainChar] = useState<number>()
  const [toDeleteElement, setToDeleteElement] =
    useState<ToDeleteElementType>()

  //manage dynamic form
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fields'
  })
  //@ts-ignore
  const onFocusElement = watch(focusedInputData?.inputname)

  const isSameLocation = useMemo(() => {
    return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value).length > 0
  }, [currentUser, notificationById])
  const isSameSector = useMemo(() => {
    return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value)[0]?.sectors.filter((i) => i.sys_id === notificationById?.incident_sector?.value).length > 0
  }, [currentUser, notificationById])
  const loggedInUserIsResponsible = useMemo(() => {
    return currentUser?.sys_id && currentUser?.sys_id ===
      notificationById?.u_assigned_to?.value
  }, [currentUser, notificationById])

  useEffect(() => {
    if (canEditActionPlan({ loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState })
      .includes(currentUser?.profile) && !statusIsArchivedOrFinished) {
      setNotAllowed(false)
    } else {
      setNotAllowed(true)
    }
  }, [loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState, statusIsArchivedOrFinished])
  //set saved actions
  useEffect(() => {
    if (actionPlans?.length) {
      const getFieldsData = actionPlans?.map(
        (plan, index) => {
          const items = listOfQuestions.map((question) => {
            return {
              [`${question}`]: plan[question].toString()
            }
          })
          return Object.assign(
            {
              sys_id: plan.sys_id,
              id: plan.sys_id
            },
            ...items
          )
        }
      )
      reset({
        fields: getFieldsData
      })
    } else if (actionPlans?.length === 0) {
      reset({
        fields: [
          {
            sys_id: '',
            id: uuidv4(),
            how: '',
            what: '',
            when: "",
            where: '',
            who: '',
            why: '',
            how_much: 'R$ 0,00'
          }
        ]
      })
    }
  }, [actionPlans])

  useEffect(() => {
    NotificationActions.getListOfActionPlan(notificationDispatch, id)
  }, [])

  useEffect(() => {
    if (focusedInputData?.inputname) {
      const charLength = onFocusElement.length
      setGetRemainChar(focusedInputData.limit - charLength)
    }
  }, [focusedInputData, onFocusElement])

  const addField = () => {
    append({
      sys_id: '',
      id: uuidv4(),
      how: '',
      what: '',
      when: "",
      where: '',
      who: '',
      why: '',
      how_much: 'R$ 0,00'
    })
  }
  const confirmeDelete = async () => {
    try {
      if (!toDeleteElement?.actionId) {
        console.error('ID do plano de ação não encontrado')
        return
      }

      // Usar endpoint batch para delete
      const deleteBatchRequest = {
        records: [toDeleteElement.actionId],
        options: {
          stopOnError: false
        }
      }

      await NotificationActions.deleteActionPlanBatch(notificationDispatch, deleteBatchRequest)

      // Recarregar dados
      NotificationActions.getListOfActionPlan(notificationDispatch, id)
    } catch (error) {
      console.error('Erro ao deletar plano de ação:', error)
    } finally {
      setDeleteWithTextModal(false)
    }
  }
  const removeField = (index: number) => {
    const rows = getValues('fields')
    const toDeleteRow = rows.find((row, idx) => {
      return idx === index
    })
    const hasData = hasNonEmptyValue(toDeleteRow)
    if (hasData && toDeleteRow.sys_id) {
      setDeleteWithTextModal(true)
      setToDeleteElement({ index, actionId: toDeleteRow.sys_id })
    } else {
      remove(index)
    }
  }

  return (
    <div data-testid="test-action-component">
      {/* modals */}
  
      <WarningModal
        icon={
          <BsExclamationTriangle
            size={50}
            className="text-errorText"
          />
        }
        customStyle={{
          titleStyle: 'text-errorText'
        }}
        title="Excluir Plano de ação"
        subTitle="Você tem certeza que deseja excluir este plano de ação já registrado? Essa ação não poderá ser desfeita."
        leftButtonAction={() => {
          setDeleteWithTextModal(false)
        }}
        rightButtonAction={() => {
          confirmeDelete()
        }}
        isOpen={deleteWithTextModal}
        handleCloseModal={() => setDeleteWithTextModal(false)}
        leftButtonText="Cancelar"
        rightButtonText="Sim, excluir"
      />
      {/* header */}
      <fieldset disabled={notAllowed}>
        {tooltipAlert && (
          <div className="px-6" data-testid="test-step4-tooltip">
            <ActionStatus text={tooltipAlert} status={1} />
          </div>
        )}
        {/* forum table */}

        <div className="flex flex-col  mb-8">
          <ActionTable
            trigger={trigger}
            fields={fields}
            notAllowed={notAllowed}
            errors={errors}
            setFocusedInputData={setFocusedInputData}
            register={register}
            setValue={setValue}
            control={control}
            removeField={removeField}
            getValues={getValues}
          />
          {errors?.fields?.length && (
            <span className="text-errorText text-xs">
              Preenchimento obrigatório
            </span>
          )}
          {focusedInputData?.inputname && (
            <span className="ml-auto mr-8">
              {getRemainChar} caracteres
            </span>
          )}
          <AddFields
            disabled={notAllowed}
            className={`${notAllowed
              ? 'text-mediumGray border-mediumGray'
              : 'text-amberAlt'
              } `}
            data-testid="test-add-line-button"
            type="button"
            onClick={() => {
              addField()
            }}
          >
            <BsPlusLg
              className={`${notAllowed ? 'text-mediumGray' : 'text-amberAlt'
                }`}
              size={8}
            />
            Adicionar linha
          </AddFields>
        </div>
      </fieldset>
    </div>
  )
}
